import useSWR, { type SWRConfiguration, type SWRResponse } from 'swr'

import { tagSwrError } from '@/src/domains/swr/utils/swrErrorKey'

// SWR's own `Key` also allows a lazy `() => Arguments`, whose falsy results cannot be seen in the type. Leaving
// it out means a skipped read is always visible here as `null`, `undefined` or `false`.
type SwrQueryKey = string | readonly [unknown, ...unknown[]] | null | undefined | false

export type SwrQueryOptions<Data = unknown> = SWRConfiguration<Data> & {
  readyToFetch?: boolean
}

// SWR's own narrowing ignores the key, so `suspense: true` promises `data` even when the read is skipped.
type IsSkippableConfig<Config> = [Config] extends [undefined]
  ? false
  : 'readyToFetch' extends keyof Config
    ? [Config] extends [{ readyToFetch: true }]
      ? false
      : true
    : false

// The Config distribution is deliberate: a union is skippable as soon as any one member is.
type IsSkippable<K, Config> = null extends K
  ? true
  : undefined extends K
    ? true
    : false extends K
      ? true
      : (Config extends unknown ? IsSkippableConfig<Config> : never) extends false
        ? false
        : true

type SwrQueryResponse<Data, Config, K> =
  IsSkippable<K, Config> extends true
    ? Omit<SWRResponse<Data, unknown, Config>, 'data'> & { data: Data | undefined }
    : SWRResponse<Data, unknown, Config>

/** Wraps `useSWR` for one endpoint while preserving its per-call-site typing. */
export const useSwrQuery = <Data, K extends SwrQueryKey, Config extends SwrQueryOptions<Data> | undefined>(
  key: K,
  fetcher: (key: K) => Promise<Data>,
  options?: Config,
  // Separate from `options` on purpose: merging them widens `Config` and kills the narrowing below.
  defaults?: SWRConfiguration<Data>,
) => {
  const { readyToFetch = true, ...config } = options ?? ({} as SwrQueryOptions)

  // Both casts are load-bearing — without them `data` is `Data | undefined` even for `suspense: true` callers.
  return useSWR(
    readyToFetch ? key : null,
    async () => {
      try {
        return await fetcher(key)
      } catch (error) {
        // Tagged here so every call site is retryable by an error boundary without repeating this.
        throw tagSwrError(error, key)
      }
    },
    {
      ...defaults,
      ...(config as SWRConfiguration<Data>),
    },
  ) as SwrQueryResponse<Data, Config, K>
}
